import type { CSSProperties, ReactNode } from "react";
import { AnchorButton } from "../ui/Button.js";
import { BrandLogo } from "../BrandLogo.js";
import { useSignupMode } from "../../hooks/useSignupMode.js";
import { signupLabel } from "../../lib/signupCopy.js";

// SSR-safe: rendered by the pre-render entry (web/src/prerender.tsx), which has no
// AuthProvider or Router context. Must never import useAuth or react-router-dom (D-36).

export function MarketingLayout({ children }: { children: ReactNode }) {
  const { mode: signupMode } = useSignupMode();
  return (
    <div className="min-h-full" style={{ "--shell-header": "calc(4rem + 1px)" } as CSSProperties}>
      <header className="sticky top-0 z-40 border-b border-line bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4">
          <a
            href="#main"
            className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:shadow-lg"
          >
            Skip to content
          </a>

          <a href="/" className="shrink-0 no-underline">
            <BrandLogo compactOnMobile className="h-8 w-auto" />
          </a>

          <nav
            className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto"
            aria-label="Main"
          >
            <a
              href="/how-it-works"
              className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-muted no-underline hover:text-ink"
            >
              How it works
            </a>
            <a
              href="/community"
              className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-muted no-underline hover:text-ink"
            >
              Browse lists
            </a>
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <AnchorButton href="/login" variant="ghost" size="sm">
              Sign in
            </AnchorButton>
            <AnchorButton href="/register" size="sm">
              {signupLabel("nav", signupMode)}
            </AnchorButton>
          </div>
        </div>
      </header>

      <main id="main">{children}</main>

      <footer className="border-t border-line bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <a href="/how-it-works" className="text-muted no-underline hover:text-ink">
              How it works
            </a>
            <a href="/packing-list" className="text-muted no-underline hover:text-ink">
              Packing
            </a>
            <a href="/shared-checklist" className="text-muted no-underline hover:text-ink">
              Shared checklist
            </a>
            <a href="/community" className="text-muted no-underline hover:text-ink">
              Community
            </a>
            <a href="/contact" className="text-muted no-underline hover:text-ink">
              Contact
            </a>
            <a href="/privacy" className="text-muted no-underline hover:text-ink">
              Privacy
            </a>
            <a href="/login" className="text-muted no-underline hover:text-ink">
              Sign in
            </a>
          </div>
          <p className="mt-6 text-xs text-muted-sm">
            &copy; {new Date().getFullYear()} NeatTick. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
